import { useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useMemo, useState } from 'react';
import { useDebounceCallback, useDebounceValue } from 'usehooks-ts';
import { useUpdateEffect } from '../../hooks/useCustomEffects';
import { getTenants } from '../../services/tenantsService';
import { IdName } from '../../types/app';
import { Tenant } from '../../types/tenants';
import EnhancedLabeledSelect from '../ui/EnhancedLabeledSelect';

type Props = {
  tenant?: IdName;
  tenants?: IdName[];
  selectedTenantId?: string;
  selectedTenantIds?: string[];
  onChangeTenantId?: (tenantId?: string) => void;
  onChangeTenant?: (tenant?: Tenant) => void;
  onChangeTenantIds?: (tenantIds?: string[]) => void;
  onChangeTenants?: (tenants?: Tenant[]) => void;
  isMulti?: boolean;
  isFilter?: boolean;
  label?: string;
  emptyOptionText?: string;
  showEmptyOption?: boolean;
  error?: React.ReactNode;
  onBlur?: () => void;
  inline?: boolean;
  clearable?: boolean;
  required?: boolean;
  maxDropdownHeight?: string;
  smallContainer?: boolean;
  dropdownOffset?: string;
};
export function TenantSelect({
  selectedTenantId,
  onChangeTenantId,
  onChangeTenant,
  selectedTenantIds,
  onChangeTenantIds,
  onChangeTenants,
  tenant,
  tenants,
  isFilter = true,
  label = 'Cliente:',
  emptyOptionText = 'Seleciona un cliente',
  showEmptyOption = false,
  error,
  onBlur,
  inline,
  clearable = false,
  required = false,
  isMulti = false,
  maxDropdownHeight,
  smallContainer,
  dropdownOffset,
}: Readonly<Props>) {
  const [tenantsList, setTenantsList] = useState<Tenant[]>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue<string | undefined>(undefined, 350);
  const [selectedTenantOption, setSelectedTenantOption] = useState<{ id: string; label: string }>();
  const [selectedTenantOptions, setSelectedTenantOptions] = useState<{ id: string; label: string }[]>();

  useUpdateEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    const loadTenants = async () => {
      startLoading();
      const { paginatedResponse, error } = await getTenants(page, debouncedSearch, 'active');
      if (!error && paginatedResponse) {
        if (page > 1 && tenantsList) {
          setTenantsList([...tenantsList, ...paginatedResponse.items]);
          setHasNextPage(paginatedResponse.hasNext);
        } else {
          setTenantsList(paginatedResponse.items);
          setHasNextPage(paginatedResponse.hasNext);
        }
      } else {
        setTenantsList(undefined);
        setHasNextPage(false);
      }
      stopLoading();
    };
    loadTenants();
  }, [page]);

  const tenantOptions = useMemo(() => {
    if (!tenantsList) return;
    const coreTenantsOptions = tenantsList.map((tenant) => ({ id: tenant._id, label: tenant.name }));
    const tenantsOptions = showEmptyOption
      ? [
          {
            id: undefined,
            label: isFilter ? 'Todos' : emptyOptionText,
          },
          ...coreTenantsOptions,
        ]
      : coreTenantsOptions;

    if (tenant) {
      if (!tenantsOptions.find((c) => c.id === tenant._id))
        tenantsOptions.push({
          id: tenant._id,
          label: tenant.name,
        });
    } else if (tenants) {
      tenants.forEach((tenant) => {
        if (!tenantsOptions.find((d) => d.id === tenant._id))
          tenantsOptions.push({
            id: tenant._id,
            label: tenant.name,
          });
      });
    }
    if (selectedTenantOption) {
      if (!tenantsOptions.find((c) => c.id === selectedTenantOption.id)) tenantsOptions.push(selectedTenantOption);
    } else if (selectedTenantOptions) {
      selectedTenantOptions.forEach((tenant) => {
        if (!tenantsOptions.find((d) => d.id === tenant.id) && tenant.id) tenantsOptions.push(tenant);
      });
    }
    return tenantsOptions;
  }, [tenantsList]);

  const onTenantChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeTenantId && onChangeTenantId(params.option.id as string);
      onChangeTenant &&
        onChangeTenant({
          _id: params.option?.id,
          name: params.option?.label,
        } as Tenant);
    } else {
      onChangeTenantId && onChangeTenantId(undefined);
      onChangeTenant && onChangeTenant(undefined);
    }
    setSelectedTenantOption(params.option as { id: string; label: string });
    setDebouncedSearch(undefined);
  };

  const onTenantsChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeTenantIds &&
        onChangeTenantIds(params.value.map((val) => val.id).filter((id) => id !== undefined) as string[]);
      onChangeTenants &&
        onChangeTenants(
          params.value
            .map((val) => ({ _id: val.id, name: val.label }))
            .filter((option) => option._id !== undefined) as Tenant[]
        );
    } else {
      onChangeTenantIds && onChangeTenantIds(undefined);
      onChangeTenants && onChangeTenants(undefined);
    }
    setSelectedTenantOptions(
      params.value.map((val) => val).filter((val) => val.id !== undefined) as { id: string; label: string }[]
    );
    setDebouncedSearch(undefined);
  };

  const fetchData = () => {
    if (hasNextPage) setPage((page ?? 0) + 1);
  };

  const handleInputChange = useDebounceCallback(function (term: string) {
    setDebouncedSearch(term);
  }, 350);

  return (
    <EnhancedLabeledSelect
      label={label}
      options={tenantOptions}
      value={
        isMulti
          ? showEmptyOption || selectedTenantIds !== undefined
            ? selectedTenantIds?.map((tenantId) => ({ id: tenantId }))
            : undefined
          : showEmptyOption || selectedTenantId !== undefined
          ? [{ id: selectedTenantId }]
          : undefined
      }
      onChange={isMulti ? onTenantsChange : onTenantChange}
      onInputChange={(e) => {
        handleInputChange(e.target.value);
      }}
      fullWidth
      infiniteScroll
      fetchData={fetchData}
      fetchDataLoading={loading}
      hasNext={hasNextPage}
      onBlur={() => {
        setDebouncedSearch(undefined);
        onBlur && onBlur();
      }}
      searchable
      inline={inline}
      inset={inline}
      error={error}
      required={required}
      clearable={clearable}
      placeholder={emptyOptionText}
      // onAdd={onAddClick}
      isMulti={isMulti}
      maxDropdownHeight={maxDropdownHeight}
      smallContainer={smallContainer}
      dropdownOffset={dropdownOffset}
    />
  );
}
